import React, { Suspense } from "react"

import { Feats } from "../../utils/organism-formatting/feats"
import { Heroes } from "../../utils/organism-formatting/heroes"

import { useContext } from "react"
import { LayoutContext } from "../Layout/Layout"
import CognitoForm from "../CognitoForm/CognitoForm"
import SuperHeroConnector from "../SuperHeroConnector/SuperHeroConnector"
import FormatAndRenderFullBackgroundHero from "../../utils/organism-formatting/heroes/FormatAndRenderFullBackgroundHero"

const FormatAndRenderHeader = React.lazy(() =>
  import("../../utils/organism-formatting/Header/FormatAndRenderHeader")
)
const FormatAndRenderLayoutStatistics = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutStatistics"
  )
)
const FormatAndRenderLayoutLogoGrid = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutLogoGrid"
  )
)
const FormatAndRenderBlockListItems = React.lazy(() =>
  import(
    "../../utils/organism-formatting/BlockListItems/FormatAndRenderBlockListItems"
  )
)
const FormatAndRenderResourceGrid = React.lazy(() =>
  import(
    "../../utils/organism-formatting/ResourceGrid/FormatAndRenderResourceGrid"
  )
)
const ContentfulLayoutWrapper = React.lazy(() =>
  import("../ContentfulLayoutWrapper/ContentfulLayoutWrapper")
)

const HeaderConnector = React.lazy(() => import("../Header/HeaderConnector"))
const FormatAndRenderTabs = React.lazy(() =>
  import("../../utils/organism-formatting/Tabs/FormatAndRenderTabs")
)
const FormatAndRenderCarousel = React.lazy(() =>
  import("../../utils/organism-formatting/Carousel/FormatAndRenderCarousel")
)
const CTAButtonsConnector = React.lazy(() =>
  import("../CTAButtons/CTAButtonsConnector")
)
const HubSpotForm = React.lazy(() => import("../HubSpot/HubSpotForm"))
const MarketoFormConnector = React.lazy(() =>
  import("../Marketo/MarketoFormConnector")
)
const FormatAndRenderCtaWithHeader = React.lazy(() =>
  import("../../utils/organism-formatting/CTA/FormatAndRenderCtaWithHeader")
)
const MetaLocatorConnector = React.lazy(() =>
  import("../MetaLocator/MetaLocatorConnector")
)
const RichTextConnector = React.lazy(() =>
  import("../RichText/RichTextConnector")
)
const FormatAndRenderNonTimeBasedListEntries = React.lazy(() =>
  import(
    "../../utils/organism-formatting/NonTimeBasedListEntries/FormatAndRenderNonTimeBasedListEntries"
  )
)
const ImageConnector = React.lazy(() => import("../Image/ImageConnector"))
const ArticleBodyConnector = React.lazy(() =>
  import("../Article/ArticleBody/ArticleBodyConnector")
)
const TwoByTwoConnector = React.lazy(() => import("../2x2/2x2Connector"))
const FormatAndRenderIconTextList = React.lazy(() =>
  import("../../utils/organism-formatting/feats/FormatAndRenderIconTextList")
)
const FormatAndRenderAccordion = React.lazy(() =>
  import("../../utils/organism-formatting/feats/FormatAndRenderAccordion")
)
const FormatAndRenderLinkList = React.lazy(() =>
  import("../../utils/organism-formatting/LinkList/FormatAndRenderLinkList")
)
const FormatAndRenderDataPoint = React.lazy(() =>
  import("../../utils/organism-formatting/DataPoint/FormatAndRenderDataPoint")
)
const FormatAndRenderHTMLEmbed = React.lazy(() =>
  import("../../utils/organism-formatting/HTMLEmbed/FormatAndRenderHTMLEmbed")
)
const FormatAndRenderInternalExternalLink = React.lazy(() =>
  import(
    "../../utils/organism-formatting/InternalExternalLink/FormatAndRenderInternalExternalLink"
  )
)
const FormatAndRenderVideo = React.lazy(() =>
  import("../../utils/organism-formatting/Video/FormatAndRenderVideo")
)
const FormatAndRenderScrollInPlace = React.lazy(() =>
  import(
    "../../utils/organism-formatting/ScrollInPlace/FormatAndRenderScrollInPlace"
  )
)
const FormatAndRenderComparisonTable = React.lazy(() =>
  import(
    "../../utils/organism-formatting/ComparisonTable/FormatAndRenderComparisonTable"
  )
)
const FormatAndRenderProductsPackage = React.lazy(() =>
  import("../../utils/organism-formatting/feats/FormatAndRenderProductsPackage")
)
const FormatAndRenderImageGallery = React.lazy(() =>
  import(
    "../../utils/organism-formatting/ImageGallery/FormatAndRenderImageGallery"
  )
)
const ProductFinder = React.lazy(() => import("../ProductFinder"))

const FormatAndRenderTimeBasedListEntries = React.lazy(() =>
  import(
    "../../utils/organism-formatting/TimeBasedListEntries/FormatAndRenderTimeBasedListEntries"
  )
)

const FormatAndRenderHeroLead = React.lazy(() => import("../../utils/organism-formatting/heroes/FormatAndRenderHeroLead"));

const FormatAndRenderLayoutFaq = React.lazy(() =>
  import(
    "../../utils/organism-formatting/LayoutComponents/FormatAndRenderLayoutFaq"
  )
)

const PageSections = ({
  pageSections,
  location,
  locale = "en",
  overrideTheme,
  nested,
  proseText,
  marginTopAndBottom,
  appData,
  ignoreFocalPoint,
  productPage,
  region,
  videoProgress,
  setVideoProgress,
  isNested,
  tab,
  siteAppData,
  currentSpaceId,
  parentIndex,
  products,
  siteIdentifier,
  isPageSection,
}) => {
  const { homepageSlug } = useContext(LayoutContext)
  return pageSections?.map((content, i) => {
    const component = renderPageSections({
      i: parentIndex || i,
      content,
      location,
      locale,
      overrideTheme,
      nested,
      proseText,
      marginTopAndBottom,
      appData,
      ignoreFocalPoint,
      productPage,
      region,
      videoProgress,
      setVideoProgress,
      isNested,
      tab,
      siteAppData,
      homepageSlug,
      currentSpaceId,
      products,
      siteIdentifier,
      isPageSection,
    })
    if (content?.anchor && !nested) {
      return (
        <div key={`section-wrapper-${i}`} id={content?.anchor}>
          {component}
        </div>
      )
    } else {
      return component
    }
  })
}

export default PageSections

const renderPageSections = ({
  i,
  content,
  location,
  locale,
  overrideTheme,
  nested,
  proseText,
  marginTopAndBottom,
  appData,
  ignoreFocalPoint,
  productPage,
  region,
  videoProgress,
  setVideoProgress,
  isNested,
  tab,
  siteAppData,
  homepageSlug,
  currentSpaceId,
  products,
  siteIdentifier,
  isPageSection,
}) => {
  // prevents suspense from trying to fire on SSR functionality, which isn't yet supported. React 18 should resolve this: https://github.com/reactwg/react-18/discussions/4
  if (content?.__typename?.includes("ContentfulHeroLead")) {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderHeroLead 
          key={`hero-lead-${i}`}
          content={content}
        />
      </Suspense>
    )
  } else if (content?.__typename?.includes("ContentfulHero")) {
    return (
      <Suspense key={content?.contentfulId}>
        <Heroes
          key={`heroes-${i}`}
          content={content}
          i={i}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
          contentful_id={content?.contentfulId}
        />
      </Suspense>
    )
  } else if (
    content?.__typename?.includes("ContentfulFeat") ||
    content?.__typename?.includes("ContentfulPullQuote")
  ) {
    return (
      <Suspense key={content?.contentfulId}>
        <Feats
          key={`feats-${i}`}
          content={content}
          i={i}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename?.includes("ContentfulBlockListItems")) {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderBlockListItems
          key={`block-list-items-${i}`}
          data={content}
          i={i}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          region={region}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
          tab={tab ? true : false}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename?.includes("ContentfulLayoutWrapper")) {
    return (
      <Suspense key={content?.contentfulId}>
        <ContentfulLayoutWrapper
          key={`contentful-layout-wrapper-${i}`}
          content={content}
          index={i}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
          siteAppData={siteAppData}
        />
      </Suspense>
    )
  } else if (content?.__typename?.includes("ContentfulCarousel")) {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderCarousel
          data={content}
          i={i}
          location={location}
          locale={locale}
          productPage={productPage ? true : false}
          key={`carousel-${i}`}
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulRichTextContent") {
    return (
      <Suspense key={content?.contentfulId}>
        <RichTextConnector
          data={content}
          key={`rich-text-connector-wrapper-${i}`}
          location={location}
          isPageSection={isPageSection === false ? false : true}
          nested={nested}
          overrideTheme={overrideTheme}
          locale={locale}
          productPage={productPage ? true : false}
          marginTopAndBottom={
            typeof marginTopAndBottom !== "undefined"
              ? marginTopAndBottom
              : true
          }
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulHeader") {
    return (
      <Suspense key={content?.contentfulId}>
        <HeaderConnector
          key={`header-connector-wrapper-${i}`}
          data={content}
          location={location}
          nested={nested}
          locale={locale}
          productPage={productPage ? true : false}
          marginTopAndBottom={
            typeof marginTopAndBottom !== "undefined"
              ? marginTopAndBottom
              : true
          }
          appData={appData}
          isPageHeader={i === 0}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulCtaButtons") {
    return (
      <Suspense key={content?.contentfulId}>
        <CTAButtonsConnector
          key={`cta-buttons-connector-wrapper-${i}`}
          data={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
          homepageSlug={homepageSlug}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulHubSpotForm") {
    const hubSpotId = siteAppData?.siteConfigurationValues?.find(val => {
      return val?.key === "hubSpot"
    })
    return (
      <Suspense key={content?.contentfulId}>
        <HubSpotForm
          formID={content?.formId}
          formTitle={content?.formTitle}
          key={`hubspot-form-${i}`}
          hubspotID={hubSpotId?.value}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulMetaLocator") {
    return (
      <Suspense key={content?.contentfulId}>
        <MetaLocatorConnector
          key={`metalocator-wrapper-${i}`}
          content={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulMarketoForm") {
    return (
      <Suspense key={content?.contentfulId}>
        <MarketoFormConnector
          data={content}
          locale={locale}
          productPage={productPage ? true : false}
          key={`marketo-form-connector-wrapper-${i}`}
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulTabs") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderTabs
          key={`format-and-render-tabs-wrapper-${i}`}
          data={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
          siteAppData={siteAppData}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulCtaWithTextAndHeader") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderCtaWithHeader
          key={`format-and-render-cta-text-header-wrapper-${i}`}
          data={content}
          overrideTheme={overrideTheme}
          nested={nested}
          locale={locale}
          productPage={productPage ? true : false}
          marginTopAndBottom={
            typeof marginTopAndBottom !== "undefined"
              ? marginTopAndBottom
              : true
          }
          appData={appData}
          currentSpaceId={currentSpaceId}
          isPageHeader={i === 0}
          products={products}
          siteIdentifier={siteIdentifier}
          contentful_id={content?.contentfulId}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulCognitoForm") {
    return (
      // <Suspense fallback={<div>Loading...</div>}>
      <CognitoForm
        data={content}
        locale={locale}
        key={`cognito-form-connector-wrapper-${i}`}
        products={products}
        siteIdentifier={siteIdentifier}
      />
      // </Suspense>
    )
  } else if (content?.__typename === "ContentfulHeader") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderHeader
          data={content}
          locale={locale}
          productPage={productPage ? true : false}
          key={`format-and-render-header-wrapper-${i}`}
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulSuperHero") {
    return (
      <SuperHeroConnector
        data={content}
        key={`super-hero-connector-wrapper-${i}`}
        location={location}
        locale={locale}
        productPage={productPage ? true : false}
        index={i}
        appData={appData}
        videoProgress={videoProgress}
        setVideoProgress={setVideoProgress}
        currentSpaceId={currentSpaceId}
        isPageHeader={i === 0}
        products={products}
        siteIdentifier={siteIdentifier}
        lazyLoad={i === 0 || i === 1 ? false : true}
        siteAppData={siteAppData}
      />
    )
  } else if (content?.__typename === "ContentfulNonTimeBasedListEntries") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderNonTimeBasedListEntries
          data={content}
          key={`ntbl-entries-${i}`}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (
    content?.__typename === "ContentfulImage" ||
    content?.__typename === "ContentfulImageWithFocalPoint"
  ) {
    return (
      <Suspense key={content?.contentfulId}>
        <ImageConnector
          key={`image-${i}`}
          data={content}
          nested={nested}
          proseText={proseText}
          locale={locale}
          productPage={productPage ? true : false}
          ignoreFocalPoint={ignoreFocalPoint}
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (
    content?.__typename === "ContentfulArticlesEventsAndProse" ||
    content?.__typename === "ContentfulNews"
  ) {
    return (
      <Suspense key={content?.contentfulId}>
        <ArticleBodyConnector
          key={`article-${i}`}
          data={content}
          usedAsComponent={true}
          location={location}
          locale={locale}
          productPage={productPage ? true : false}
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "Contentful2X2") {
    return (
      <Suspense key={content?.contentfulId}>
        <TwoByTwoConnector
          key={`two-by-two-connector-${i}`}
          data={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          isNested={isNested}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulIconTextList") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderIconTextList
          key={`icon-text-list-${i}`}
          overrideTheme={overrideTheme}
          data={content}
          nested={isNested}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulAccordion") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderAccordion
          key={`accordion-${i}`}
          data={content}
          overrideTheme={overrideTheme}
          nested={nested}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          currentSpaceId={currentSpaceId}
          siteIdentifier={siteIdentifier}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
          homepageSlug={homepageSlug}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulLinkList") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderLinkList
          key={`link-list-${i}`}
          data={content}
          location={location}
          overrideTheme={overrideTheme}
          nested={nested}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulDataPoint") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderDataPoint
          key={`data-point-${i}`}
          data={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulFullBackgroundHero") {
    return (
      <FormatAndRenderFullBackgroundHero
        index={i}
        key={`full-background-hero-${i}`}
        data={content}
        location={location}
        locale={locale}
        appData={appData}
        productPage={productPage ? true : false}
        currentSpaceId={currentSpaceId}
        products={products}
        siteIdentifier={siteIdentifier}
        videoProgress={videoProgress}
        setVideoProgress={setVideoProgress}
      />
    )
  } else if (content?.__typename === "ContentfulHtmlEmbed") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderHTMLEmbed
          key={`html-embed-${i}`}
          data={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
          scriptMode={content?.scriptMode || false}
        />
      </Suspense>
    )
  } else if (
    content?.__typename === "ContentfulExternalLink" ||
    content?.__typename === "ContentfulPageBase"
  ) {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderInternalExternalLink
          key={`rich-text-link-${i}`}
          data={content}
          location={location}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulComparisonTable") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderComparisonTable
          data={content}
          locale={locale}
          productPage={productPage ? true : false}
          key={`comparison-table-${i}`}
          appData={appData}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (
    content?.__typename === "ContentfulVimeoOrYouTubeVideo" ||
    content?.__typename === "ContentfulVidyardVideo"
  ) {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderVideo
          data={content}
          nested={nested}
          key={`video-${i}`}
          appData={appData}
          videoProgress={videoProgress}
          setVideoProgress={setVideoProgress}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulScrollInPlace") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderScrollInPlace
          data={content}
          key={`sip-${i}`}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulProductsPackage") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderProductsPackage
          data={content}
          key={`products-package-${i}`}
          appData={appData}
          productPage={productPage ? true : false}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulImageGallery") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderImageGallery
          data={content}
          key={`image-gallery-${i}`}
          appData={appData}
          productPage={productPage ? true : false}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulProductFinder") {
    return (
      <Suspense key={content?.contentfulId}>
        <ProductFinder
          data={content}
          locale={locale}
          key={`product-finder-${i}`}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulResourceGrid") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderResourceGrid
          data={content}
          key={`resource-grid-wrapper-${i}`}
          location={location}
          locale={locale}
          marginTopAndBottom={
            typeof marginTopAndBottom !== "undefined"
              ? marginTopAndBottom
              : true
          }
          appData={appData}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulLayoutFaq") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderLayoutFaq
          data={content}
          key={`faq-${i}`}
          locale={locale}
          currentSpaceId={currentSpaceId}
          siteIdentifier={siteIdentifier}
          homepageSlug={homepageSlug}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulTimeBasedLists") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderTimeBasedListEntries
          data={content}
          key={`tbl-entries-${i}`}
          locale={locale}
          appData={appData}
          productPage={productPage ? true : false}
          currentSpaceId={currentSpaceId}
          products={products}
          siteIdentifier={siteIdentifier}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulLayoutStatistics") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderLayoutStatistics
          data={content}
          key={`statistics-${i}`}
          locale={locale}
          currentSpaceId={currentSpaceId}
          siteIdentifier={siteIdentifier}
          homepageSlug={homepageSlug}
        />
      </Suspense>
    )
  } else if (content?.__typename === "ContentfulLayoutLogoGrid") {
    return (
      <Suspense key={content?.contentfulId}>
        <FormatAndRenderLayoutLogoGrid
          data={content}
          key={`logo-grid-${i}`}
          locale={locale}
          currentSpaceId={currentSpaceId}
          siteIdentifier={siteIdentifier}
          homepageSlug={homepageSlug}
        />
      </Suspense>
    )
  } else {
    return null
  }
}
